/**
 * DecreeProceduresTab — Ustawienia → Procedury dekretacji.
 *
 * Katalog AUTORSKICH procedur SQL (`decree_procedures`) uruchamianych z belki dekretacji na
 * fakturach zakupu/sprzedaży (`DecreeProcedureRunControls.tsx`, NIE modyfikowany tutaj) — mirror
 * strukturalny `RecordProceduresTab.tsx` (SQL editor = zwykły `Textarea` + `InsertExampleButton`,
 * mapowanie błędów SQL przez `decree-procedures/error-keys.ts`, sekcja „Testuj" w modalu
 * formularza) SKRZYŻOWANY z `DecreeTemplatesTab.tsx` (pole `companyId` NULLABLE — `null` =
 * procedura dla WSZYSTKICH firm tenanta, `documentClass` IMMUTABLE po utworzeniu, JEDEN kod
 * permission `documents.<klasa faktury>.manage_accounting` dla odczytu i zapisu — bez per-scope gate
 * na cały tab, tylko ukrycie przycisków akcji, jak `DecreeTemplatesTab`).
 *
 * RÓŻNICA vs oba wzorce: `declaredParams` (dodatkowe parametry SQL) CELOWO bez UI — silnik
 * (`apps/api/lib/documents/decree-procedure-executor.ts`) zawsze woła procedurę z pustym
 * `paramValues` (kontekst rezerwowy `:document_id`/`:company_id`/`:document_class`/`:user_id`
 * jest jedynym kanałem danych), więc edytor własnych parametrów nie miałby żadnego realnego
 * efektu w runtime — pominięty jako martwa funkcjonalność, nie dług.
 *
 * Sekcja „Testuj" (w modalu formularza) woła `POST /decree-procedures/test-sql` na SUROWYM,
 * jeszcze niezapisanym SQL z formularza + `documentId` REALNEJ, zapisanej faktury wskazanej
 * ręcznie przez autora procedury (zwykły `Input` na UUID — mirror `RecordProcedureFormModal`
 * „Kontekst zaawansowany": autor wkleja identyfikator próbny zamiast korzystać z ciężkiego
 * pickera; w odróżnieniu od kartotek dekret NIE MA bezpiecznego placeholdera — silnik czyta
 * realne dane faktury, więc ID musi wskazywać istniejący, zapisany dokument właściwej klasy).
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.confirm/alert — ConfirmDialog + useToast.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { InsertExampleButton } from '@/components/InsertExampleButton';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { Textarea } from '@/components/Textarea';
import { SqlField } from '@/components/ui/SqlField';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { decreeProcedureSqlErrorKey } from '@/lib/decree-procedures/error-keys';
import {
  buildSqlParamCompletions,
  DECREE_PROCEDURE_RESERVED_PARAMS,
} from '@/lib/sql-editor/params';
import { useTenantSqlSchema } from '@/lib/sql-editor/schema';
import { useSqlTestDiagnostics } from '@/lib/sql-editor/test-error';
import { useCompanies } from '@/lib/use-permissions';

// Dekretacja obejmuje OBIE klasy faktur, a te mają od F24 osobne kody uprawnień —
// wystarczy prawo po jednej ze stron (mirror `lib/documents/accounting-permission` w API).
const MANAGE_PERMS = [
  'documents.purchase_invoice.manage_accounting',
  'documents.sale_invoice.manage_accounting',
] as const;

const DOCUMENT_CLASSES = ['purchase_invoice', 'sale_invoice'] as const;
type DecreeProcedureDocumentClass = (typeof DOCUMENT_CLASSES)[number];

/** Mirror `apps/api/lib/validators/uuid.ts` — walidacja WYŁĄCZNIE po stronie klienta (front-run
 *  przed wysłaniem, żeby nie strzelać oczywistym 404 DOCUMENT_NOT_FOUND za literówkę). */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ============================================================================
// Typy
// ============================================================================

interface DecreeProcedureRow {
  id: string;
  companyId: string | null;
  documentClass: DecreeProcedureDocumentClass;
  name: string;
  description: string | null;
  sql: string;
  isActive: boolean;
  gcrecord: string | null;
}

interface DecreeProceduresListResponse {
  procedures: DecreeProcedureRow[];
}

interface TestSqlResultLine {
  groupNo: number;
  side: 'debit' | 'credit';
  mainAccount: string;
  subAccount1: string | null;
  subAccount2: string | null;
  subAccount3: string | null;
  subAccount4: string | null;
  subAccount5: string | null;
  amount: string;
  description: string | null;
}

interface TestSqlResponse {
  lines: TestSqlResultLine[];
}

/** Konto skondensowane do jednej kolumny podglądu (Syntetyka-Pozycja1-…) — segmenty puste
 * pomijane. Wyłącznie prezentacyjne, mirror `GetStringAccount()` z WF; edytor/odczyt dekretu
 * pokazują 6 osobnych kolumn, tu chodzi o zwięzły podgląd wyniku testu. */
function formatTestResultAccount(line: TestSqlResultLine): string {
  return [
    line.mainAccount,
    line.subAccount1,
    line.subAccount2,
    line.subAccount3,
    line.subAccount4,
    line.subAccount5,
  ]
    .filter((segment): segment is string => !!segment)
    .join('-');
}

export interface DecreeProcedureFormValues {
  name: string;
  description: string;
  documentClass: DecreeProcedureDocumentClass;
  /** '' = wszystkie firmy (`companyId: null` w payloadzie). */
  companyId: string;
  sql: string;
  isActive: boolean;
}

function emptyFormValues(): DecreeProcedureFormValues {
  return {
    name: '',
    description: '',
    documentClass: 'purchase_invoice',
    companyId: '',
    sql: '',
    isActive: true,
  };
}

function rowToFormValues(row: DecreeProcedureRow): DecreeProcedureFormValues {
  return {
    name: row.name,
    description: row.description ?? '',
    documentClass: row.documentClass,
    companyId: row.companyId ?? '',
    sql: row.sql,
    isActive: row.isActive,
  };
}

function errorCode(err: unknown): string | null {
  return isAxiosError(err)
    ? ((err.response?.data as { error?: string } | undefined)?.error ?? null)
    : null;
}

// ============================================================================
// Modal create/edit + sekcja „Testuj"
// ============================================================================

interface DecreeProcedureFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: DecreeProcedureFormValues;
  companies: { id: string; legalName: string }[];
  saving?: boolean;
  onSubmit: (values: DecreeProcedureFormValues) => void;
  onClose: () => void;
}

function DecreeProcedureFormModal({
  open,
  mode,
  initialValues,
  companies,
  saving = false,
  onSubmit,
  onClose,
}: DecreeProcedureFormModalProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [testDocumentId, setTestDocumentId] = useState('');
  const [testDocumentIdError, setTestDocumentIdError] = useState<string | null>(null);
  const sqlSchema = useTenantSqlSchema(open);
  const sqlParams = useMemo(
    () => buildSqlParamCompletions(t, DECREE_PROCEDURE_RESERVED_PARAMS),
    [t],
  );

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<TestSqlResponse>('/decree-procedures/test-sql', {
        documentId: testDocumentId.trim(),
        sql: values.sql.trim(),
      });
      return res.data.lines;
    },
  });

  const sqlDiagnostics = useSqlTestDiagnostics(values.sql, testMutation);
  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setErrors({});
      setTestDocumentId('');
      setTestDocumentIdError(null);
      testMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues]);

  if (!open) return null;

  function set<K extends keyof DecreeProcedureFormValues>(
    key: K,
    value: DecreeProcedureFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!values.name.trim()) {
      next.name = t('decreeProcedures.form.errors.nameRequired');
    } else if (values.name.trim().length > 200) {
      next.name = t('decreeProcedures.form.errors.nameLength');
    }
    if (!values.sql.trim()) {
      next.sql = t('decreeProcedures.form.errors.sqlRequired');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({ ...values, name: values.name.trim(), description: values.description.trim() });
  }

  function handleTest() {
    setTestDocumentIdError(null);
    if (!UUID_REGEX.test(testDocumentId.trim())) {
      setTestDocumentIdError(t('decreeProcedures.form.errors.testDocumentIdInvalid'));
      return;
    }
    if (!values.sql.trim()) return;
    testMutation.mutate();
  }

  const sqlExample =
    values.documentClass === 'sale_invoice'
      ? t('decreeProcedures.form.sqlExampleSaleInvoice')
      : t('decreeProcedures.form.sqlExamplePurchaseInvoice');

  const testError = testMutation.isError
    ? (() => {
        const err = testMutation.error;
        const data =
          isAxiosError(err) && err.response?.data && typeof err.response.data === 'object'
            ? (err.response.data as {
                error?: string;
                paramName?: string;
                accountNumber?: string;
                sqlState?: string;
              })
            : null;
        const code = data?.error;
        const translated = t(decreeProcedureSqlErrorKey(code), {
          paramName: data?.paramName ?? '',
          accountNumber: data?.accountNumber ?? '',
        });
        // EXECUTION_FAILED NIE niesie już surowego komunikatu Postgresa (przeciek danych —
        // kontrakt backendu zwraca wyłącznie `sqlState`) — generyczny tekst + kod SQLSTATE
        // w nawiasie, wystarczające dla autora SQL do diagnozy bez ekspozycji treści błędu.
        if (code === 'EXECUTION_FAILED' && data?.sqlState) {
          return `${translated} (${data.sqlState})`;
        }
        return translated;
      })()
    : null;
  const testLines = testMutation.data ?? null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(
        mode === 'create' ? 'decreeProcedures.form.titleCreate' : 'decreeProcedures.form.titleEdit',
      )}
      // Szerokie okno: dane procedury w dwóch kolumnach, pod nimi pole SQL i „Testuj” na całą
      // szerokość (bez przewijania).
      size="2xl"
      guardUnsaved
      // Klasa dokumentu, firma i „Wstaw przykład" zmieniają dane kliknięciem (bez zdarzeń
      // wpisywania) — „zmieniono" liczymy porównaniem wartości ze stanem startowym.
      dirty={JSON.stringify(values) !== JSON.stringify(initialValues)}
      // W trakcie zapisu nie da się zamknąć okna tłem ani Esc (jak „Anuluj").
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="decree-procedure-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div data-testid="decree-procedure-form">
        <div
          className="grid grid-cols-1 items-start gap-x-6 gap-y-3 lg:grid-cols-2"
          data-testid="decree-procedure-form-layout"
        >
          <div className="space-y-3">
            <FormField label={t('decreeProcedures.form.name')} error={errors.name}>
              <Input
                value={values.name}
                onChange={(e) => {
                  set('name', e.target.value);
                  setErrors((prev) => ({ ...prev, name: '' }));
                }}
                placeholder={t('decreeProcedures.form.namePlaceholder')}
                maxLength={200}
                data-testid="decree-procedure-form-name"
              />
            </FormField>

            <FormField label={t('decreeProcedures.form.description')}>
              <Textarea
                value={values.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder={t('decreeProcedures.form.descriptionPlaceholder')}
                rows={2}
                data-testid="decree-procedure-form-description"
              />
            </FormField>
          </div>

          <div className="space-y-3">
            <FormField
              label={t('decreeProcedures.form.documentClass')}
              hint={
                mode === 'edit' ? t('decreeProcedures.form.documentClassImmutableHint') : undefined
              }
            >
              <Dropdown
                value={values.documentClass}
                onChange={(v) => set('documentClass', v as DecreeProcedureDocumentClass)}
                disabled={mode === 'edit'}
                data-testid="decree-procedure-form-class"
                options={DOCUMENT_CLASSES.map((cls) => ({
                  value: cls,
                  label: t(`decreeProcedures.documentClasses.${cls}`),
                }))}
              />
            </FormField>

            <FormField
              label={t('decreeProcedures.form.company')}
              hint={
                mode === 'edit'
                  ? t('decreeProcedures.form.companyImmutableHint')
                  : t('decreeProcedures.form.companyHint')
              }
            >
              <Dropdown
                value={values.companyId}
                onChange={(v) => set('companyId', v)}
                disabled={mode === 'edit'}
                data-testid="decree-procedure-form-company"
                options={[
                  { value: '', label: t('decreeProcedures.form.companyAllOption') },
                  ...companies.map((c) => ({ value: c.id, label: c.legalName })),
                ]}
              />
            </FormField>

            <Checkbox
              checked={values.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
              label={t('decreeProcedures.form.isActive')}
              data-testid="decree-procedure-form-is-active"
            />
          </div>

          {/* Pole SQL na całą szerokość okna (obie kolumny). */}
          <div className="space-y-1 lg:col-span-2">
            <div className="flex justify-end">
              <InsertExampleButton
                currentValue={values.sql}
                example={sqlExample}
                onInsert={(value) => {
                  set('sql', value);
                  setErrors((prev) => ({ ...prev, sql: '' }));
                }}
                data-testid="decree-procedure-form-sql-example"
              />
            </div>
            <FormField
              label={t('decreeProcedures.form.sqlLabel')}
              hint={t('decreeProcedures.form.sqlHint')}
              error={errors.sql}
            >
              <SqlField
                value={values.sql}
                onChange={(value) => {
                  set('sql', value);
                  setErrors((prev) => ({ ...prev, sql: '' }));
                }}
                schema={sqlSchema}
                parameters={sqlParams}
                diagnostics={sqlDiagnostics}
                placeholder={t('common.examplePlaceholder', { example: sqlExample })}
                aria-label={t('decreeProcedures.form.sqlLabel')}
                minHeight="200px"
                data-testid="decree-procedure-form-sql"
              />
            </FormField>
          </div>

          <div
            className="space-y-2 rounded-lg border border-border-primary bg-bg-secondary p-3 lg:col-span-2"
            data-testid="decree-procedure-form-test-section"
          >
            <p className="text-sm font-medium text-text-primary">
              {t('decreeProcedures.form.testTitle')}
            </p>
            <p className="text-xs text-text-tertiary">{t('decreeProcedures.form.testHint')}</p>

            <FormField
              label={t('decreeProcedures.form.testDocumentIdLabel')}
              error={testDocumentIdError ?? undefined}
            >
              <Input
                value={testDocumentId}
                onChange={(e) => {
                  setTestDocumentId(e.target.value);
                  setTestDocumentIdError(null);
                }}
                placeholder={t('decreeProcedures.form.testDocumentIdPlaceholder')}
                data-testid="decree-procedure-form-test-document-id"
              />
            </FormField>

            <Button
              variant="secondary"
              size="sm"
              onClick={handleTest}
              loading={testMutation.isPending}
              disabled={
                testMutation.isPending ||
                values.sql.trim().length === 0 ||
                testDocumentId.trim().length === 0
              }
              data-testid="decree-procedure-form-test-btn"
            >
              {t('decreeProcedures.form.testButton')}
            </Button>

            {testError && (
              <p
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
                data-testid="decree-procedure-form-test-error"
              >
                {testError}
              </p>
            )}

            {testLines &&
              (testLines.length === 0 ? (
                <p
                  className="text-xs text-text-tertiary"
                  data-testid="decree-procedure-form-test-result-empty"
                >
                  {t('decreeProcedures.form.testResult.empty')}
                </p>
              ) : (
                <div
                  className="overflow-x-auto rounded-md border border-border-primary"
                  data-testid="decree-procedure-form-test-result"
                >
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border-primary bg-bg-primary">
                        <th className="px-2 py-1 text-left">
                          {t('decreeProcedures.form.testResult.colGroup')}
                        </th>
                        <th className="px-2 py-1 text-left">
                          {t('decreeProcedures.form.testResult.colSide')}
                        </th>
                        <th className="px-2 py-1 text-left">
                          {t('decreeProcedures.form.testResult.colAccount')}
                        </th>
                        <th className="px-2 py-1 text-left">
                          {t('decreeProcedures.form.testResult.colAmount')}
                        </th>
                        <th className="px-2 py-1 text-left">
                          {t('decreeProcedures.form.testResult.colDescription')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {testLines.map((line, index) => (
                        <tr
                          key={`${line.groupNo}-${line.side}-${index}`}
                          className="border-b border-border-primary last:border-0"
                        >
                          <td className="px-2 py-1">{line.groupNo}</td>
                          <td className="px-2 py-1">
                            {t(`decreeProcedures.form.testResult.side.${line.side}`)}
                          </td>
                          <td className="px-2 py-1">{formatTestResultAccount(line)}</td>
                          <td className="px-2 py-1">{line.amount}</td>
                          <td className="px-2 py-1">{line.description ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// Główny komponent
// ============================================================================

export function DecreeProceduresTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const canManage =
    me?.user.isSuperUser === true ||
    MANAGE_PERMS.some((code) => me?.permissions.includes(code) ?? false);

  const companiesQ = useCompanies();
  const companies = useMemo(() => companiesQ.data?.companies ?? [], [companiesQ.data]);

  // Filtr firmy jest OPCJONALNY (inaczej niż `DecreeTemplatesTab`) — backend (Model B,
  // `resolveListCompanyScope`) bez `companyId` zwraca unię firm z dostępem + procedury
  // `company_id IS NULL` („wszystkie firmy"), więc lista działa też bez wyboru firmy.
  const [companyFilter, setCompanyFilter] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DecreeProcedureRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DecreeProcedureRow | null>(null);
  const [pendingRestore, setPendingRestore] = useState<DecreeProcedureRow | null>(null);

  const listQ = useQuery({
    queryKey: ['decree-procedures', 'settings-list', companyFilter, showDeleted] as const,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (companyFilter) params.set('companyId', companyFilter);
      if (showDeleted) params.set('include_deleted', 'true');
      const qs = params.toString();
      const res = await api.get<DecreeProceduresListResponse>(
        `/decree-procedures${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    retry: false,
  });

  const procedures = listQ.data?.procedures ?? [];

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['decree-procedures', 'settings-list'] });
    // Picker „Uruchom procedurę dekretacji" na stronach faktur
    // (queryKey ['decree-procedures', 'picker', ...], `DecreeProcedureRunControls.tsx`).
    queryClient.invalidateQueries({ queryKey: ['decree-procedures', 'picker'] });
  }

  function mapMutationErrorTitle(err: unknown): string {
    const code = errorCode(err);
    switch (code) {
      case 'PROCEDURE_NAME_TAKEN':
        return t('decreeProcedures.toast.nameTaken');
      case 'COMPANY_NOT_FOUND':
        return t('decreeProcedures.toast.companyNotFound');
      case 'PROCEDURE_NOT_FOUND':
        return t('decreeProcedures.toast.notFound');
      case 'NOT_DELETED':
        return t('decreeProcedures.toast.notDeleted');
      case 'PERMISSION_DENIED':
        return t('decreeProcedures.toast.permissionDenied');
      default: {
        // create/update re-walidują SQL strukturalnie PRZED zapisem — te same kody `SQL_<code>`
        // co sekcja „Testuj" (patrz docstring `error-keys.ts`). Bez tego mapowania user dostawał
        // tylko generyczny „nie udało się zapisać", tracąc realny powód.
        if (code?.startsWith('SQL_')) {
          const data = isAxiosError(err)
            ? (err.response?.data as { message?: string; paramName?: string } | undefined)
            : undefined;
          const translated = t(decreeProcedureSqlErrorKey(code), {
            paramName: data?.paramName ?? '',
          });
          return data?.message ? `${translated} ${data.message}` : translated;
        }
        return t('decreeProcedures.toast.genericError');
      }
    }
  }

  const createMutation = useMutation({
    mutationFn: async (values: DecreeProcedureFormValues) => {
      const body = {
        companyId: values.companyId || null,
        documentClass: values.documentClass,
        name: values.name,
        description: values.description.trim() || undefined,
        sql: values.sql,
        isActive: values.isActive,
      };
      const res = await api.post<{ procedure: DecreeProcedureRow }>('/decree-procedures', body);
      return res.data.procedure;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('decreeProcedures.toast.created') });
      setCreateOpen(false);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapMutationErrorTitle(err) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: DecreeProcedureFormValues }) => {
      const patch = {
        name: values.name,
        description: values.description.trim() || null,
        sql: values.sql,
        isActive: values.isActive,
      };
      const res = await api.patch<{ procedure: DecreeProcedureRow }>(
        `/decree-procedures/${id}`,
        patch,
      );
      return res.data.procedure;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('decreeProcedures.toast.updated') });
      setEditing(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapMutationErrorTitle(err) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/decree-procedures/${id}`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('decreeProcedures.toast.deleted') });
      setPendingDelete(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapMutationErrorTitle(err) });
      setPendingDelete(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/decree-procedures/${id}/restore`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('decreeProcedures.toast.restored') });
      setPendingRestore(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapMutationErrorTitle(err) });
      setPendingRestore(null);
    },
  });

  return (
    <div data-testid="decree-procedures-tab">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('decreeProcedures.list.title')}
          </h2>
          <p className="text-xs text-text-tertiary">{t('decreeProcedures.list.subtitle')}</p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
            data-testid="decree-procedure-new-btn"
          >
            {t('decreeProcedures.list.newProcedure')}
          </Button>
        )}
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            {t('decreeProcedures.list.companyFilterLabel')}
          </label>
          <Dropdown
            value={companyFilter ?? ''}
            onChange={(v) => setCompanyFilter(v || null)}
            data-testid="decree-procedures-company-filter"
            options={[
              { value: '', label: t('decreeProcedures.list.companyFilterAllOption') },
              ...companies.map((c) => ({ value: c.id, label: c.legalName })),
            ]}
          />
        </div>
        <Checkbox
          checked={showDeleted}
          onChange={(e) => setShowDeleted(e.target.checked)}
          label={t('decreeProcedures.list.showDeleted')}
          data-testid="decree-procedures-show-deleted"
        />
      </div>

      {listQ.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : listQ.isError ? (
        <Card padding="md">
          <p className="text-sm text-danger-600">
            {errorCode(listQ.error) === 'PERMISSION_DENIED'
              ? t('decreeProcedures.list.noPermission')
              : t('decreeProcedures.list.loadError')}
          </p>
        </Card>
      ) : procedures.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('decreeProcedures.list.empty')}</p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">{t('decreeProcedures.list.col.name')}</th>
                  <th className="px-4 py-2 text-left">
                    {t('decreeProcedures.list.col.documentClass')}
                  </th>
                  <th className="px-4 py-2 text-left">{t('decreeProcedures.list.col.company')}</th>
                  <th className="px-4 py-2 text-left">{t('decreeProcedures.list.col.status')}</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {procedures.map((row) => {
                  const isDeleted = row.gcrecord != null;
                  const companyName = row.companyId
                    ? (companies.find((c) => c.id === row.companyId)?.legalName ?? row.companyId)
                    : null;
                  return (
                    <tr key={row.id} data-testid={`decree-procedure-row-${row.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-text-primary">{row.name}</div>
                        {row.description && (
                          <div className="text-xs text-text-tertiary">{row.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge color="accent">
                          {t(`decreeProcedures.documentClasses.${row.documentClass}`)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {companyName ?? (
                          <Badge color="gray">{t('decreeProcedures.companyAllBadge')}</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          {!row.isActive && (
                            <Badge color="gray">{t('decreeProcedures.badges.inactive')}</Badge>
                          )}
                          {isDeleted && (
                            <Badge color="red">{t('decreeProcedures.badges.deleted')}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {canManage && (
                          <div className="flex items-center justify-end gap-1">
                            {isDeleted ? (
                              <IconButton
                                size="sm"
                                onClick={() => setPendingRestore(row)}
                                aria-label={t('common.restore')}
                                data-testid={`decree-procedure-restore-${row.id}`}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </IconButton>
                            ) : (
                              <>
                                <IconButton
                                  size="sm"
                                  onClick={() => setEditing(row)}
                                  aria-label={t('common.edit')}
                                  data-testid={`decree-procedure-edit-${row.id}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </IconButton>
                                <IconButton
                                  variant="danger"
                                  size="sm"
                                  onClick={() => setPendingDelete(row)}
                                  aria-label={t('common.delete')}
                                  data-testid={`decree-procedure-delete-${row.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </IconButton>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <DecreeProcedureFormModal
        open={createOpen}
        mode="create"
        initialValues={emptyFormValues()}
        companies={companies}
        saving={createMutation.isPending}
        onSubmit={(values) => createMutation.mutate(values)}
        onClose={() => setCreateOpen(false)}
      />

      <DecreeProcedureFormModal
        open={editing !== null}
        mode="edit"
        initialValues={editing ? rowToFormValues(editing) : emptyFormValues()}
        companies={companies}
        saving={updateMutation.isPending}
        onSubmit={(values) => {
          if (editing) updateMutation.mutate({ id: editing.id, values });
        }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('decreeProcedures.confirmDelete.title')}
        message={
          pendingDelete
            ? t('decreeProcedures.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingRestore !== null}
        variant="primary"
        title={t('decreeProcedures.confirmRestore.title')}
        message={
          pendingRestore
            ? t('decreeProcedures.confirmRestore.message', { name: pendingRestore.name })
            : ''
        }
        confirmLabel={t('common.restore')}
        confirming={restoreMutation.isPending}
        onConfirm={() => {
          if (pendingRestore) restoreMutation.mutate(pendingRestore.id);
        }}
        onClose={() => setPendingRestore(null)}
      />
    </div>
  );
}
